import {ParseResult, Schema} from "effect";

import {ShortId} from "./ShortId.js";
import type {ShortId as ShortIdType, ShortIdData} from "./ShortId.js";

// ── Interface ───────────────────────────────────────────────────────────

/**
 * A slug~shortId pair with an optional scope UID type witness.
 *
 * The `ScopeUid` type parameter mirrors the witness on {@link ShortIdData}
 * so that a `UrlSlugId<AccountUid>` can only be created from a
 * `ShortIdData<AccountUid>`.
 */
interface UrlSlugId<ScopeUid extends string = string> {
    readonly slug: string;
    readonly shortId: ShortIdType;
    readonly shortIdData?: ShortIdData<ScopeUid> | undefined;
}

// ── Functions ───────────────────────────────────────────────────────────

/**
 * Parse a combined `slug~shortId` string into its parts.
 * Splits on the LAST occurrence of the prefix. Returns undefined if no
 * prefix is found or the shortId portion is invalid.
 */
function parse(combined: string): UrlSlugId | undefined {
    const lastIndex = combined.lastIndexOf(ShortId.config.prefix);
    if (lastIndex === -1) {
        return undefined;
    }

    const slug = combined.slice(0, lastIndex);
    const shortIdRaw = combined.slice(lastIndex + ShortId.config.prefix.length);

    const shortId = ShortId.fromStringSafe(shortIdRaw);
    if (shortId === undefined) {
        return undefined;
    }

    return {slug, shortId};
}

/**
 * Format a UrlSlugId back into the `slug~shortId` string representation.
 */
function format<ScopeUid extends string = string>(id: UrlSlugId<ScopeUid>): string {
    return `${id.slug}${ShortId.config.prefix}${id.shortId}`;
}

/**
 * Create a UrlSlugId from a slug and a branded ShortId.
 */
function make(slug: string, shortId: ShortIdType): UrlSlugId;
function make<ScopeUid extends string>(slug: string, shortIdData: ShortIdData<ScopeUid>): UrlSlugId<ScopeUid>;
function make<ScopeUid extends string>(
    slug: string,
    shortIdOrData: ShortIdType | ShortIdData<ScopeUid>,
): UrlSlugId<ScopeUid> {
    if (typeof shortIdOrData === "string") {
        return {slug, shortId: shortIdOrData};
    }
    return {slug, shortId: shortIdOrData.value, shortIdData: shortIdOrData};
}

/**
 * Alias for format — returns the `slug~shortId` string representation.
 */
function toString<ScopeUid extends string = string>(id: UrlSlugId<ScopeUid>): string {
    return format(id);
}

// ── Effect Schema transform: String <-> { slug, shortId } ──────────────

const urlSlugIdSchema = Schema.transformOrFail(
    Schema.String,
    Schema.Struct({slug: Schema.String, shortId: ShortId.schema}),
    {
        strict: true,
        decode: (combined, _options, ast) => {
            const result = parse(combined);
            if (result === undefined) {
                return ParseResult.fail(new ParseResult.Type(ast, combined, `Invalid UrlSlugId: '${combined}'`));
            }
            return ParseResult.succeed(result);
        },
        encode: (id) => ParseResult.succeed(`${id.slug}${ShortId.config.prefix}${id.shortId}`),
    },
);

// ── Companion object ────────────────────────────────────────────────────

const UrlSlugId = {
    parse,
    format,
    make,
    toString,
    schema: urlSlugIdSchema,
} as const;

export {UrlSlugId};
export type {UrlSlugId as UrlSlugIdType};
